import { Data } from 'cio/utils/analytics/transforms';
import AttributeModel from 'cio/models/attribute';
import EventModel from 'cio/models/event';
import RouteInfoWithAttributes from '@ember/routing/-private/route-info-with-attributes';

const SOURCES = {
  setupGuide: 'Onboarding',
  campaignOverview: 'Campaign Overview',
  campaignTrigger: 'Campaign Trigger',
  personOverview: 'Person Overview Tab',
  personAttributes: 'Person Attributes Tab',
  personSegments: 'Person Segments Tab',
  personDevices: 'Person Devices Tab',
  personDrafts: 'Person Drafts Tab',
  personSent: 'Person Sent Tab',
  personActivity: 'Person Activity Tab',
  personJourneys: 'Person Journeys Tab',
  dataIndexAttributes: 'Data Index Attributes List',
  dataIndexEvents: 'Data Index Events List',
  dataIndexEvent: 'Data Index Event Page',
  dataIndexAttribute: 'Data Index Attribute Page',
  logs: 'Activity Logs',
  transactionalIndex: 'Transactional List View',
  transactionalMessageOverviewIndex: 'Transactional Overview Tab',
  transactionalMessageOverviewMetrics: 'Transactional Metrics Tab',
  sqlImportsCreate: 'SQL Sync Creation Flow',
  sqlImportsQuery: 'SQL Sync Query',
  sqlImportsSettings: 'SQL Sync Settings',
} as const;

export function generateSource(data: Data): (typeof SOURCES)[keyof typeof SOURCES] | undefined {
  if (!data?.route?.name) {
    return;
  }

  if (data.route.name.startsWith('setup-guide')) {
    return SOURCES.setupGuide;
  }

  if (data.route.name.startsWith('logs')) {
    return SOURCES.logs;
  }

  if (
    data.route.name.startsWith('campaigns.campaign.overview.trigger') ||
    data.route.name.startsWith('campaigns.campaign.setup.trigger')
  ) {
    return SOURCES.campaignTrigger;
  }

  if (data.route.name.startsWith('campaigns.campaign.overview')) {
    return SOURCES.campaignOverview;
  }

  if (data.route.name.startsWith('person.index')) {
    return SOURCES.personOverview;
  }

  if (data.route.name.startsWith('person.attributes')) {
    return SOURCES.personAttributes;
  }

  if (data.route.name.startsWith('person.segments')) {
    return SOURCES.personSegments;
  }

  if (data.route.name.startsWith('person.devices')) {
    return SOURCES.personDevices;
  }

  if (data.route.name.startsWith('person.drafts')) {
    return SOURCES.personDrafts;
  }

  if (data.route.name.startsWith('person.sent')) {
    return SOURCES.personSent;
  }

  if (data.route.name.startsWith('person.activity')) {
    return SOURCES.personActivity;
  }

  if (data.route.name.startsWith('person.subjects')) {
    return SOURCES.personJourneys;
  }

  if (data.route.name.startsWith('data-index.type.item')) {
    return ((data.route as RouteInfoWithAttributes).attributes as AttributeModel | EventModel)?.type === 'event'
      ? SOURCES.dataIndexEvent
      : SOURCES.dataIndexAttribute;
  }

  if (data.route.name.startsWith('data-index.type.list')) {
    let typeRoute = data.route.find((item) => item.name === 'data-index.type');
    if (!typeRoute) {
      return;
    }

    return (typeRoute.params.type as 'events' | 'attributes') === 'events'
      ? SOURCES.dataIndexEvents
      : SOURCES.dataIndexAttributes;
  }

  if (data.route.name.startsWith('transactional.index')) {
    return SOURCES.transactionalIndex;
  }

  if (data.route.name.startsWith('transactional.message.overview.index')) {
    return SOURCES.transactionalMessageOverviewIndex;
  }

  if (data.route.name.startsWith('transactional.message.overview.metrics')) {
    return SOURCES.transactionalMessageOverviewMetrics;
  }

  if (data.route.name.startsWith('sql-imports.new')) {
    return SOURCES.sqlImportsCreate;
  }

  if (data.route.name.startsWith('sql-imports.import.query')) {
    return SOURCES.sqlImportsQuery;
  }

  if (data.route.name.startsWith('sql-imports.import.settings')) {
    return SOURCES.sqlImportsSettings;
  }

  return;
}
